import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import Button from '../../../buttons/Button';
import MobilePrinterContent from './MobilePrinterContent';
import { useMobilePrinter } from '../../../../hooks/useMobilePrinter';
import LoadingIndicator from '../../../layout/LoadingIndicator/LoadingIndicator';
import styles from './MobilePrinter.css';
import { MOBILE_PRINTER_TEST_CONNECTION_STATUS } from '../../../../constants';
import { getMobilePrinterListFeatureEnabled } from '../../../../utils/mobilePrinter';

const useStyles = makeStyles({
  mobilePrinterRoot: {
    '@media (min-width:500px)': {
      width: '100%',
    },
  },
});

const MobilePrinter = () => {
  const { t } = useTranslation();
  const translationRoot = 'MOBILE_PRINTER';
  const printerInputRef = useRef<HTMLInputElement>(null);

  const classes = useStyles();
  const mobilePrinterListFeatureEnabled = !!getMobilePrinterListFeatureEnabled();
  const shouldFetchPrinterList = mobilePrinterListFeatureEnabled;

  const {
    shouldDisableConnectButton,
    manualPrinterNumber,
    isPrinterConnected,
    connectedPrinterNumber,
    mobilePrinterList,
    mobilePrinterListError,
    mobilePrinterListLoading,
    selectedPrinterFromList,
    handlePrinterConnectionToggle,
    handleManualPrinterNumberChange,
    handlePrinterSelectionFromList,
    testConnectionStatus,
    testConnectionMessage,
    testConnectionIsLoading,
    completePrinterConnection,
  } = useMobilePrinter({
    shouldFetchPrinterList,
    shouldShowToast: true,
  });

  const getButtonClickHandler = () => {
    if (isPrinterConnected) {
      return handlePrinterConnectionToggle();
    }
    if (testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.warning) {
      return completePrinterConnection();
    }
    return handlePrinterConnectionToggle();
  };

  const getButtonLabel = () => {
    if (isPrinterConnected) {
      return t(`${translationRoot}.DISCONNECT`);
    }
    if (testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.warning) {
      return t(`${translationRoot}.CONTINUE`);
    }
    return t(`${translationRoot}.CONNECT`);
  };

  useEffect(() => {
    if (testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.success) {
      completePrinterConnection();
    }
  }, [testConnectionStatus, completePrinterConnection]);

  // Focus input when API fails and manual printer value exists
  useEffect(() => {
    if (testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.error && manualPrinterNumber && printerInputRef.current) {
      printerInputRef.current.focus();
    }
  }, [testConnectionStatus, manualPrinterNumber]);

  return (
    <div className={styles.mainContent}>
      <h2>{t(`${translationRoot}.CONNECT_TO_MOBILE_PRINTER_TITLE`)}</h2>

      {testConnectionIsLoading ? (
        <div className={styles.loadingContainer}>
          <LoadingIndicator />
        </div>
      ) : (
        <>
          {testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.error && (
            <div className={styles.testPrintConnectionError}>{testConnectionMessage}</div>
          )}

          {testConnectionStatus === MOBILE_PRINTER_TEST_CONNECTION_STATUS.warning && (
            <div className={styles.testPrintConnectionWarning}>{testConnectionMessage}</div>
          )}

          <div className={styles.mobilePrinterContentContainer}>
            <MobilePrinterContent
              ref={printerInputRef}
              isPrinterConnected={isPrinterConnected}
              manualPrinterNumber={manualPrinterNumber}
              handleManualPrinterNumberChange={handleManualPrinterNumberChange}
              translationRoot={translationRoot}
              inputRootClass={classes.mobilePrinterRoot}
              mobilePrinterListError={mobilePrinterListError}
              mobilePrinterList={mobilePrinterList}
              mobilePrinterListLoading={mobilePrinterListLoading}
              handlePrinterSelectionFromList={handlePrinterSelectionFromList}
              selectedPrinterFromList={selectedPrinterFromList}
              connectedPrinterNumber={connectedPrinterNumber}
              mobilePrinterListFeatureEnabled={mobilePrinterListFeatureEnabled}
              onSubmit={shouldDisableConnectButton || testConnectionIsLoading ? undefined : getButtonClickHandler}
            />
            <div className={styles.paginationContainer}>
              <Button className={styles.footerButton} onClick={getButtonClickHandler} disabled={shouldDisableConnectButton || testConnectionIsLoading}>
                {getButtonLabel()}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobilePrinter;
